import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../lib/api';
import { Icons } from '../components/icons';

type StorageForm = {
  backend: 'local' | 'oss';
  local_upload_dir: string;
  oss: {
    endpoint: string;
    region: string;
    bucket: string;
    access_key: string;
    secret_key: string;
    access_key_set?: boolean;
    access_key_masked?: string;
    secret_key_set?: boolean;
    use_path_style: boolean;
    cdn_base: string;
  };
};

const emptyForm = (): StorageForm => ({
  backend: 'local',
  local_upload_dir: '/var/lib/gridorbit/uploads',
  oss: {
    endpoint: 'https://s3.cn-north-1.jcloudcs.com',
    region: 'cn-north-1',
    bucket: '',
    access_key: '',
    secret_key: '',
    use_path_style: true,
    cdn_base: '',
  },
});

export function StorageSettings() {
  const [form, setForm] = useState<StorageForm>(emptyForm());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    setMsg('');
    apiFetch('/api/v1/platform/storage-settings').then(d => {
      if (d.code === 0 && d.data) {
        const data = d.data as Record<string, any>;
        setForm({
          backend: data.backend === 'oss' ? 'oss' : 'local',
          local_upload_dir: data.local_upload_dir || '/var/lib/gridorbit/uploads',
          oss: {
            endpoint: data.oss?.endpoint || '',
            region: data.oss?.region || 'cn-north-1',
            bucket: data.oss?.bucket || '',
            access_key: '',
            secret_key: '',
            access_key_set: data.oss?.access_key_set,
            access_key_masked: data.oss?.access_key_masked,
            secret_key_set: data.oss?.secret_key_set,
            use_path_style: data.oss?.use_path_style !== false,
            cdn_base: data.oss?.cdn_base || '',
          },
        });
      } else setMsg(d.message || '加载失败');
      setLoading(false);
    }).catch(() => { setMsg('网络错误'); setLoading(false); });
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    setMsg('');
    const d = await apiFetch('/api/v1/platform/storage-settings', {
      method: 'PUT',
      body: {
        backend: form.backend,
        local_upload_dir: form.local_upload_dir.trim(),
        oss: {
          endpoint: form.oss.endpoint.trim(),
          region: form.oss.region.trim(),
          bucket: form.oss.bucket.trim(),
          access_key: form.oss.access_key.trim() || undefined,
          secret_key: form.oss.secret_key.trim() || undefined,
          use_path_style: form.oss.use_path_style,
          cdn_base: form.oss.cdn_base.trim(),
        },
      },
    });
    setSaving(false);
    if (d.code === 0) {
      setMsg('已保存。修改存储后端后请重启 sop-designer 与 document 服务，或设置环境变量 STORAGE_BACKEND。');
      load();
    } else setMsg(d.message || '保存失败');
  };

  if (loading) return <div style={{ padding: 24 }}>加载中…</div>;

  return (
    <div style={{ maxWidth: 720 }}>
      <h2 style={{ fontSize: 18, fontWeight: 650, marginBottom: 8 }}>对象存储配置</h2>
      <p style={{ fontSize: 13, color: 'var(--brand-text-muted)', marginBottom: 20, lineHeight: 1.6 }}>
        支持<strong>本地磁盘</strong>（私有化/离线部署）与<strong>京东云 OSS</strong>（S3 兼容）两种模式，按租户前缀隔离并按套餐 GB 配额计量。
      </p>
      {msg && <div style={{ marginBottom: 12, fontSize: 13, color: msg.startsWith('已保存') ? '#16a34a' : '#dc2626' }}>{msg}</div>}

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-title">存储后端</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
          {[
            { value: 'local' as const, label: '本地磁盘', desc: '文件写入服务器 UPLOAD_DIR，适合本地/私有化部署' },
            { value: 'oss' as const, label: '京东云 OSS', desc: 'S3 兼容对象存储，适合 SaaS 云端部署' },
          ].map(opt => (
            <label key={opt.value} style={{
              display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 12px', borderRadius: 8, cursor: 'pointer',
              border: '1px solid ' + (form.backend === opt.value ? 'var(--brand-primary)' : 'var(--brand-border)'),
              background: form.backend === opt.value ? 'var(--brand-primary-light)' : 'transparent', fontSize: 13,
            }}>
              <input type="radio" name="storage-backend" checked={form.backend === opt.value}
                onChange={() => setForm(f => ({ ...f, backend: opt.value }))} />
              <div><div style={{ fontWeight: 600 }}>{opt.label}</div><div style={{ fontSize: 11, color: 'var(--brand-text-muted)', marginTop: 2 }}>{opt.desc}</div></div>
            </label>
          ))}
        </div>

        {form.backend === 'local' && (
          <div className="form-group">
            <label>本地上传目录</label>
            <input className="form-input" value={form.local_upload_dir}
              onChange={e => setForm(f => ({ ...f, local_upload_dir: e.target.value }))}
              placeholder="/var/lib/gridorbit/uploads" />
          </div>
        )}

        {form.backend === 'oss' && (
          <>
            <div className="form-group"><label>Endpoint</label>
              <input className="form-input" value={form.oss.endpoint} onChange={e => setForm(f => ({ ...f, oss: { ...f.oss, endpoint: e.target.value } }))} placeholder="https://s3.cn-north-1.jcloudcs.com" /></div>
            <div className="form-group"><label>Region</label>
              <input className="form-input" value={form.oss.region} onChange={e => setForm(f => ({ ...f, oss: { ...f.oss, region: e.target.value } }))} placeholder="cn-north-1" /></div>
            <div className="form-group"><label>Bucket</label>
              <input className="form-input" value={form.oss.bucket} onChange={e => setForm(f => ({ ...f, oss: { ...f.oss, bucket: e.target.value } }))} /></div>
            <div className="form-group"><label>Access Key</label>
              <input className="form-input" type="password" value={form.oss.access_key}
                onChange={e => setForm(f => ({ ...f, oss: { ...f.oss, access_key: e.target.value } }))}
                placeholder={form.oss.access_key_set ? `已配置 ${form.oss.access_key_masked || '****'}` : ''} /></div>
            <div className="form-group"><label>Secret Key</label>
              <input className="form-input" type="password" value={form.oss.secret_key}
                onChange={e => setForm(f => ({ ...f, oss: { ...f.oss, secret_key: e.target.value } }))}
                placeholder={form.oss.secret_key_set ? '已配置，留空不修改' : ''} /></div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 12 }}>
              <input type="checkbox" checked={form.oss.use_path_style} onChange={e => setForm(f => ({ ...f, oss: { ...f.oss, use_path_style: e.target.checked } }))} />
              Path-Style 访问（京东云推荐开启）
            </label>
            <div className="form-group"><label>CDN 域名（可选）</label>
              <input className="form-input" value={form.oss.cdn_base} onChange={e => setForm(f => ({ ...f, oss: { ...f.oss, cdn_base: e.target.value } }))} placeholder="https://cdn.example.com" /></div>
          </>
        )}
      </div>

      <button className="btn btn-primary" disabled={saving} onClick={save}>{saving ? '保存中…' : '保存配置'}</button>
    </div>
  );
}
